import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Ticket, Copy, Trash2, Plus, KeyRound, Check } from "lucide-react";
import api, { formatApiErrorDetail } from "../api";

const copyText = async (text, label) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(label);
  } catch {
    toast.error("Impossible de copier — copie manuellement : " + text);
  }
};

export const InviteCodesSection = () => {
  const [codes, setCodes] = useState([]);
  const [maxUses, setMaxUses] = useState("1");
  const [expiresDays, setExpiresDays] = useState("");
  const [creating, setCreating] = useState(false);
  const load = useCallback(() => api.get("/admin/invite-codes").then(({ data }) => setCodes(data)), []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/admin/invite-codes", {
        max_uses: parseInt(maxUses, 10),
        expires_days: expiresDays ? parseInt(expiresDays, 10) : null,
      });
      toast.success(`Code ${data.code} créé !`);
      copyText(data.link, "Lien d'invitation copié — envoie-le par texto 📱");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
    setCreating(false);
  };

  const remove = async (c) => {
    if (!window.confirm(`Désactiver le code ${c.code} ? Il ne pourra plus être utilisé.`)) return;
    try {
      await api.delete(`/admin/invite-codes/${c.id}`);
      toast.success(`Code ${c.code} désactivé`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="bg-[#14141A] border border-[#2E2E3A] p-4 space-y-3" data-testid="invite-codes-section">
      <p className="font-display text-xl text-white flex items-center gap-2">
        <Ticket size={17} className="text-[#FF3B30]" /> CODES D'INVITATION
      </p>
      <p className="text-[#B0B0B4] text-xs">Génère un code, copie le lien et envoie-le par texto — ton ami s'inscrit immédiatement, sans courriel.</p>
      <div className="flex gap-2">
        <select data-testid="invite-code-max-uses" value={maxUses} onChange={(e) => setMaxUses(e.target.value)}
          className="set-input flex-1">
          <option value="1">Usage unique</option>
          <option value="5">5 utilisations</option>
          <option value="10">10 utilisations</option>
          <option value="0">Illimité</option>
        </select>
        <input data-testid="invite-code-expires" type="number" min="1" placeholder="Expire (jours)"
          value={expiresDays} onChange={(e) => setExpiresDays(e.target.value)} className="set-input w-32" />
        <button data-testid="invite-code-create-button" disabled={creating} onClick={create}
          className="h-10 px-3 bg-[#FF3B30] text-white text-sm font-semibold flex items-center gap-1.5 hover:bg-[#e0322a] transition-colors disabled:opacity-40">
          <Plus size={14} /> Générer
        </button>
      </div>
      {codes.length > 0 && (
        <div className="divide-y divide-white/5 border border-[#2E2E3A]">
          {codes.map((c) => (
            <div key={c.id} data-testid={`invite-code-${c.code}`} className="px-3 py-2 flex items-center gap-2">
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-mono font-bold tracking-widest">{c.code}</p>
                <p className="text-[#B0B0B4] text-[11px] truncate">
                  {c.max_uses === 0 ? `${c.uses} utilisation${c.uses > 1 ? "s" : ""} · illimité` : `${c.uses}/${c.max_uses} utilisé`}
                  {c.expires_at && ` · expire le ${c.expires_at.slice(0, 10)}`}
                  {c.expired && " · ⛔ EXPIRÉ"}
                  {c.used_by.length > 0 && ` · ${c.used_by.join(", ")}`}
                </p>
              </div>
              <button data-testid={`copy-invite-link-${c.code}`}
                onClick={() => copyText(c.link, "Lien d'invitation copié 📱")}
                title="Copier le lien d'invitation"
                className="text-[#B0B0B4] hover:text-white p-2 transition-colors"><Copy size={14} /></button>
              <button data-testid={`delete-invite-code-${c.code}`} onClick={() => remove(c)}
                className="text-[#555] hover:text-[#FF3B30] p-2 transition-colors"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const TempPasswordModal = ({ result, onClose }) => (
  <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="temp-password-modal">
    <div className="w-full max-w-sm bg-[#14141A] border border-[#FF3B30]/40 p-5 space-y-3">
      <p className="font-display text-2xl text-white flex items-center gap-2">
        <KeyRound size={18} className="text-[#FF3B30]" /> MOT DE PASSE TEMPORAIRE
      </p>
      <p className="text-[#ccc] text-sm">
        Nouveau mot de passe pour <strong className="text-white">{result.name}</strong> — transmets-le-lui par texto.
        Il devra en choisir un nouveau à sa prochaine connexion.
      </p>
      <div className="flex items-center gap-2 bg-black/40 border border-white/15 px-3 py-2.5">
        <p data-testid="temp-password-value" className="flex-1 text-white font-mono text-lg tracking-wider select-all">{result.temp_password}</p>
        <button data-testid="copy-temp-password-button"
          onClick={() => copyText(result.temp_password, "Mot de passe temporaire copié")}
          className="text-[#B0B0B4] hover:text-white p-1.5 transition-colors"><Copy size={16} /></button>
      </div>
      <p className="text-amber-400 text-xs">⚠️ Il ne sera plus jamais affiché — copie-le maintenant.</p>
      <button data-testid="temp-password-done-button" onClick={onClose}
        className="w-full h-10 bg-[#FF3B30] text-white font-display text-lg tracking-wide hover:bg-[#e0322a] transition-colors flex items-center justify-center gap-2">
        <Check size={15} /> C'EST NOTÉ
      </button>
    </div>
  </div>
);
